import { parseCSV } from './csv';
import { normalizeManagerName } from './managerStats';

export { normalizeManagerName };

const WEEKLY_MATCHUPS_CSV = '/data/weekly_matchups.csv';
const PLAYOFF_DATA_CSV = '/data/playoff_data.csv';
const CHAMPIONSHIP_ROUNDS = new Set(['Quarterfinal', 'Semifinal', 'Championship']);
const PLAYOFF_ROUND_ORDER = { Quarterfinal: 1, Semifinal: 2, Championship: 3 };

function toNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toInteger(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function round(value, places = 2) {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

function median(values) {
  if (!values.length) return 0;
  const sorted = values.slice().sort((first, second) => first - second);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

function longestWinStreak(games) {
  let current = 0;
  let longest = 0;
  games
    .slice()
    .sort((first, second) => first.year - second.year || first.week - second.week)
    .forEach((game) => {
      if (game.result === 'W') {
        current += 1;
        longest = Math.max(longest, current);
      } else {
        current = 0;
      }
    });
  return longest;
}

function summarizeGames(games) {
  const scores = games.map((game) => game.score);
  const pointsFor = scores.reduce((sum, score) => sum + score, 0);
  const pointsAgainst = games.reduce((sum, game) => sum + game.opponentScore, 0);
  const average = games.length ? pointsFor / games.length : 0;
  const variance = games.length
    ? scores.reduce((sum, score) => sum + ((score - average) ** 2), 0) / games.length
    : 0;
  const sortedHigh = games.slice().sort((first, second) => second.score - first.score);
  const sortedLow = games.slice().sort((first, second) => first.score - second.score);
  const wins = games.filter((game) => game.result === 'W').length;
  const losses = games.filter((game) => game.result === 'L').length;
  const ties = games.filter((game) => game.result === 'T').length;
  const closeGames = games.filter((game) => Math.abs(game.margin) <= 5);

  return {
    games: games.length,
    wins,
    losses,
    ties,
    pointsFor: round(pointsFor),
    pointsAgainst: round(pointsAgainst),
    averageScore: round(average),
    medianScore: round(median(scores)),
    standardDeviation: round(Math.sqrt(variance)),
    highScore: sortedHigh[0] || null,
    lowScore: sortedLow[0] || null,
    largestWin: games
      .filter((game) => game.result === 'W')
      .sort((first, second) => second.margin - first.margin)[0] || null,
    largestLoss: games
      .filter((game) => game.result === 'L')
      .sort((first, second) => first.margin - second.margin)[0] || null,
    gamesAtOrAbove200: games.filter((game) => game.score >= 200).length,
    gamesAtOrAbove250: games.filter((game) => game.score >= 250).length,
    closeGames: {
      total: closeGames.length,
      wins: closeGames.filter((game) => game.result === 'W').length,
      losses: closeGames.filter((game) => game.result === 'L').length,
      ties: closeGames.filter((game) => game.result === 'T').length,
    },
    longestWinStreak: longestWinStreak(games),
  };
}

function regularGameFor(manager, row) {
  const team1 = normalizeManagerName(row['Team1 Manager']);
  const team2 = normalizeManagerName(row['Team2 Manager']);
  if (team1 !== manager && team2 !== manager) return null;

  const managerIsTeam1 = team1 === manager;
  const score = toNumber(row[managerIsTeam1 ? 'Team1 Score' : 'Team2 Score']);
  const opponentScore = toNumber(row[managerIsTeam1 ? 'Team2 Score' : 'Team1 Score']);
  if (score === null || opponentScore === null) return null;

  return {
    year: toInteger(row.Year || row.Season),
    week: toInteger(row.Week),
    team: row[managerIsTeam1 ? 'Team1 Name' : 'Team2 Name'],
    opponent: managerIsTeam1 ? team2 : team1,
    opponentTeam: row[managerIsTeam1 ? 'Team2 Name' : 'Team1 Name'],
    score,
    opponentScore,
    margin: round(score - opponentScore),
    result: score > opponentScore ? 'W' : score < opponentScore ? 'L' : 'T',
  };
}

function playoffGameFor(manager, row) {
  if (!CHAMPIONSHIP_ROUNDS.has(row.Round)) return null;
  const team1 = normalizeManagerName(row['Team1 Manager']);
  const team2 = normalizeManagerName(row['Team2 Manager']);
  if (team1 !== manager && team2 !== manager) return null;

  const managerIsTeam1 = team1 === manager;
  const score = toNumber(row[managerIsTeam1 ? 'Team1 Score' : 'Team2 Score']);
  const opponentScore = toNumber(row[managerIsTeam1 ? 'Team2 Score' : 'Team1 Score']);
  if (score === null || opponentScore === null) return null;

  return {
    year: toInteger(row.Year),
    week: PLAYOFF_ROUND_ORDER[row.Round],
    round: row.Round,
    team: row[managerIsTeam1 ? 'Team1 Name' : 'Team2 Name'],
    opponent: managerIsTeam1 ? team2 : team1,
    opponentTeam: row[managerIsTeam1 ? 'Team2 Name' : 'Team1 Name'],
    score,
    opponentScore,
    margin: round(score - opponentScore),
    result: score > opponentScore ? 'W' : score < opponentScore ? 'L' : 'T',
  };
}

export function buildManagerGameStats(manager, matchups = [], playoffs = []) {
  const normalizedManager = normalizeManagerName(manager);
  const regularGames = matchups
    .map((row) => regularGameFor(normalizedManager, row))
    .filter(Boolean);
  const postseasonGames = playoffs
    .map((row) => playoffGameFor(normalizedManager, row))
    .filter(Boolean);

  return {
    regular: summarizeGames(regularGames),
    postseason: {
      ...summarizeGames(postseasonGames),
      finalsAppearances: postseasonGames.filter((game) => game.round === 'Championship').length,
    },
  };
}

async function loadCsv(path, fetchImpl) {
  const response = await fetchImpl(path);
  if (!response.ok) throw new Error(`Failed to load ${path}`);
  return parseCSV(await response.text());
}

export async function loadManagerGameStats(manager, fetchImpl = fetch) {
  const [matchups, playoffs] = await Promise.all([
    loadCsv(WEEKLY_MATCHUPS_CSV, fetchImpl),
    loadCsv(PLAYOFF_DATA_CSV, fetchImpl),
  ]);
  return buildManagerGameStats(manager, matchups, playoffs);
}
